import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Gamepad2, ShoppingBag, User as UserIcon } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { count, setOpen: setCartOpen } = useCart();
  const { user, login } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { label: 'Shop', href: '/shop', route: true },
    { label: 'Flavors', href: '/#flavors' },
    { label: 'Ingredients', href: '/#ingredients' },
    { label: 'Partner', href: '/#partner' },
    { label: 'Leaderboard', href: '/leaderboard', route: true }
  ];

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all ${scrolled ? 'bg-[#FFF4E6]/90 backdrop-blur border-b border-black/10' : ''}`}>
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-3 md:py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <img
            src="https://customer-assets-cm19k8pv.emergentagent.net/job_trucrunch-games/artifacts/0ug68r65_download.png"
            alt="TruCrunch"
            className="h-10 md:h-12 w-auto group-hover:scale-105 transition-transform"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {links.map((l) => (
            l.route ? (
              <Link key={l.label} to={l.href} className="font-brico text-sm font-semibold uppercase tracking-wide hover:text-[#E63946] transition-colors">{l.label}</Link>
            ) : (
              <a key={l.label} href={l.href} className="font-brico text-sm font-semibold uppercase tracking-wide hover:text-[#E63946] transition-colors">{l.label}</a>
            )
          ))}
          <button onClick={() => setCartOpen(true)} className="relative p-2 rounded-full bg-white crunch-shadow-sm hover:-translate-y-0.5 transition-transform">
            <ShoppingBag size={18} />
            {count > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#E63946] text-white text-[10px] font-bold flex items-center justify-center">{count}</span>}
          </button>
          {user ? (
            <Link to="/account" className="flex items-center gap-2 p-1 pr-3 rounded-full bg-white crunch-shadow-sm hover:-translate-y-0.5 transition-transform" title={user.email}>
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#E63946] text-white font-display text-sm flex items-center justify-center">
                  {(user.name || user.email || '?').charAt(0).toUpperCase()}
                </div>
              )}
              <span className="text-xs font-bold uppercase tracking-widest">Account</span>
            </Link>
          ) : (
            <button onClick={login} className="p-2 rounded-full bg-white crunch-shadow-sm hover:-translate-y-0.5 transition-transform" title="Sign in">
              <UserIcon size={18} />
            </button>
          )}
          <Link to="/play" className="btn-chunky primary">
            <Gamepad2 size={18} /> Play
          </Link>
        </nav>

        <div className="md:hidden flex items-center gap-2">
          <button onClick={() => setCartOpen(true)} className="relative p-2 rounded-full bg-white crunch-shadow-sm">
            <ShoppingBag size={18} />
            {count > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#E63946] text-white text-[10px] font-bold flex items-center justify-center">{count}</span>}
          </button>
          <button onClick={() => setOpen(!open)} className="p-2 rounded-full bg-white crunch-shadow-sm">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-[#FFF4E6] border-t border-black/10 px-5 py-6 flex flex-col gap-4">
          {links.map((l) => (
            l.route
              ? <Link key={l.label} to={l.href} onClick={() => setOpen(false)} className="font-brico text-lg font-semibold uppercase">{l.label}</Link>
              : <a key={l.label} href={l.href} onClick={() => setOpen(false)} className="font-brico text-lg font-semibold uppercase">{l.label}</a>
          ))}
          {user ? (
            <Link to="/account" onClick={() => setOpen(false)} className="font-brico text-lg font-semibold uppercase flex items-center gap-2">
              <UserIcon size={16} /> My Account
            </Link>
          ) : (
            <button onClick={() => { setOpen(false); login(); }} className="font-brico text-lg font-semibold uppercase flex items-center gap-2 text-left">
              <UserIcon size={16} /> Sign In
            </button>
          )}
          <Link to="/play" onClick={() => setOpen(false)} className="btn-chunky primary justify-center">
            <Gamepad2 size={18} /> Play the Game
          </Link>
        </div>
      )}
    </header>
  );
};

export default Navbar;
